import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { constants } from "@qgiv/core-js";
import { submitParticipantAction } from "@qgiv/p2p-registration";
import * as formViews from "./constants/formViews";
import * as messageLocations from "./constants/messageLocations";
import { getInitialValues, getValidationSchema } from "./formik";
import ConnectedAccountFormMessage from "./ConnectedAccountFormMessage";
import ConnectedAccountPageContent from "./ConnectedAccountPageContent";

const AccountFormContent = ({
    registrationAccount,
    updateRegistrationAccountFormView,
    updateRegistrationAccountSubmitted,
    updateRegistrationAccountSubmittedMessage,
    updateRegistrationAccountIsLoggedIn,
    updateRegistrationAccountHasActivatedAccount,
}) => {
    const dispatch = useDispatch();
    const {
        ENUMS: { GlobalAccountAction },
    } = constants;

    // Set the donor form view based on the initialGlobalAccountAction
    const setInitialDonorFormView = (_registrationAccount) => {
        const {
            form: { initialGlobalAccountAction },
        } = _registrationAccount;

        if (initialGlobalAccountAction === GlobalAccountAction.RESET_PASSWORD) {
            return formViews.RESET_PASSWORD;
        }
        if (
            initialGlobalAccountAction === GlobalAccountAction.ACTIVATE_ACCOUNT
        ) {
            return formViews.ACTIVATE_ACCOUNT;
        }

        return formViews.SIGN_IN;
    };

    const initialDonorFormView = setInitialDonorFormView(registrationAccount);

    // Add the donor action to the Redux store
    useEffect(() => {
        dispatch(updateRegistrationAccountFormView(initialDonorFormView));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmitParticipantActionSuccess = (
        _registrationAccount,
        values,
        setSubmitting,
    ) => {
        const {
            form: { location },
            urls: { myFundraisingDashboard },
        } = _registrationAccount;
        const redirectToFundraisingDashboard = () => {
            window.location.href = myFundraisingDashboard;
        };

        // Store the data that was used with the form submission in state
        dispatch(updateRegistrationAccountSubmitted(values));

        // Remove Formik's submitting state from the form
        // If this is moved to after the view is updated React will throw an
        // error about updating an unmounted component
        setSubmitting(false);

        // If the Account form is displayed on the Receipt page
        if (location === "Receipt") {
            if (
                values.Global_Account_Action ===
                GlobalAccountAction.ACTIVATE_ACCOUNT
            ) {
                const passwordSavedMessage = {
                    message: [
                        "Password saved. Your fundraising account is now active!",
                    ],
                    type: "success",
                    location: messageLocations.RECEIPT_HEADER,
                };

                // If the user activated their account
                dispatch(updateRegistrationAccountIsLoggedIn(true));
                // Triggers the UI to replace the Activate Account CTA with the
                // Start Fundraising CTA
                dispatch(updateRegistrationAccountHasActivatedAccount(true));
                // Display the Password Saved message
                dispatch(
                    updateRegistrationAccountSubmittedMessage(
                        passwordSavedMessage,
                    ),
                );
            }
        }

        // If the Account form is displayed on the Account page
        if (location === "Account") {
            // If present, remove any pre-existing error messages
            dispatch(updateRegistrationAccountSubmittedMessage({}));

            if (
                values.Global_Account_Action ===
                GlobalAccountAction.SEND_RESET_PASSWORD_EMAIL
            ) {
                // If the user triggered a reset password email
                // Display the help is on the way message to the user
                dispatch(
                    updateRegistrationAccountFormView(
                        formViews.SEND_RESET_PASSWORD_EMAIL_CONFIRMATION,
                    ),
                );
            }

            if (
                values.Global_Account_Action ===
                GlobalAccountAction.ACTIVATE_ACCOUNT
            ) {
                // If the activated their account
                dispatch(updateRegistrationAccountIsLoggedIn(true));
                dispatch(updateRegistrationAccountHasActivatedAccount(true));
                redirectToFundraisingDashboard();
            }

            if (
                values.Global_Account_Action === GlobalAccountAction.SIGN_IN ||
                values.Global_Account_Action ===
                    GlobalAccountAction.RESET_PASSWORD
            ) {
                // If the user signed in or reset their password
                dispatch(updateRegistrationAccountIsLoggedIn(true));
                redirectToFundraisingDashboard();
            }
        }
    };

    const handleSubmitParticipantActionError = (errors) => {
        const { message } = errors;
        const errorMessage = {
            message,
            type: "error",
            location: messageLocations.ACCOUNT_FORM,
        };

        // Display the error message
        dispatch(updateRegistrationAccountSubmittedMessage(errorMessage));
    };

    // Placeholder form submission handler
    // The actions argument represents the "formikBag".
    // The "formikBag" is an object that with a subset of helper props & methods.
    // actions.setSubmitting sets isSubmitting to false if the onSubmit function is synchronous.
    // It Does not need to be called if the function is asynchronous.
    const onSubmit = (values, actions) => {
        const {
            urls: { participantAction },
        } = registrationAccount;
        const { setSubmitting } = actions;

        // Send data to the participant action endpoint
        submitParticipantAction(participantAction, values)
            .then((response) => {
                const {
                    data: { success },
                } = response;

                if (success) {
                    handleSubmitParticipantActionSuccess(
                        registrationAccount,
                        values,
                        setSubmitting,
                    );
                }
            })
            .catch((errors) => {
                // eslint-disable-next-line no-console
                console.error(errors);

                // Remove the submitting state that triggers the processing
                // animation from the form
                setSubmitting(false);

                handleSubmitParticipantActionError(errors);
            });
    };

    const formikProps = {
        // initialDonorFormView is used to set the Global_Account_Action which
        // is used for conditional validation
        initialValues: getInitialValues(registrationAccount),
        validationSchema: getValidationSchema(),
        // Populates the errors object and disables the submit button on mount
        validateOnMount: true,
        // Allows the state of the submit button to be updated on character
        // change instead of waiting on a click outside the field
        validateOnChange: true,
        onSubmit,
    };

    return (
        <Formik {...formikProps}>
            <Form>
                <ConnectedAccountFormMessage />
                <ConnectedAccountPageContent />
            </Form>
        </Formik>
    );
};

AccountFormContent.propTypes = {
    // Refined version of the registrationAccount object from state with only
    // the keys that affect this component or its dependencies
    registrationAccount: PropTypes.shape({
        contact: PropTypes.shape({
            email: PropTypes.string.isRequired,
        }).isRequired,
        form: PropTypes.shape({
            initialGlobalAccountAction: PropTypes.number.isRequired,
            location: PropTypes.string.isRequired,
            uniqueIdentifier: PropTypes.string.isRequired,
        }).isRequired,
        urls: PropTypes.shape({
            myFundraisingDashboard: PropTypes.string.isRequired,
            participantAction: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    updateRegistrationAccountFormView: PropTypes.func.isRequired,
    updateRegistrationAccountSubmitted: PropTypes.func.isRequired,
    updateRegistrationAccountSubmittedMessage: PropTypes.func.isRequired,
    updateRegistrationAccountIsLoggedIn: PropTypes.func.isRequired,
    updateRegistrationAccountHasActivatedAccount: PropTypes.func.isRequired,
};

export default AccountFormContent;
